import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fmtPrice } from "@/data/catalog";

const Box = ({ children }) => (
  <main className="nv-container py-24 text-center">
    <div className="nv-fade-up mx-auto max-w-lg rounded-3xl bg-black p-12 text-white">{children}</div>
  </main>
);

export function PaymentSuccess() {
  const { t, i18n } = useTranslation();
  const [sp] = useSearchParams();
  const { clear } = useCart();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) return setState("error");
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        if (data.payment_status === "paid") { setOrder(data.order); clear(); return setState("paid"); }
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch { /* retry */ }
      if (++tries < 8) setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (state === "checking") return (
    <Box><Loader2 size={48} className="mx-auto animate-spin text-[#c6ff00]" /><p data-testid="payment-checking" className="mt-6 font-display text-2xl font-bold">{t("payment.checking")}</p></Box>
  );
  if (state === "paid") return (
    <Box>
      <CheckCircle2 size={56} className="mx-auto text-[#c6ff00]" />
      <h1 data-testid="payment-success" className="mt-6 font-display text-3xl font-bold sm:text-4xl">{t("payment.success")}</h1>
      <p className="mt-3 text-white/70">{t("checkout.successSub", { name: order?.shipping?.firstName || "", email: order?.email || "" })}</p>
      <p className="mt-6 text-xs uppercase tracking-widest text-white/50">{t("checkout.orderNo")}</p>
      <p data-testid="order-number" className="font-display text-2xl font-bold text-[#c6ff00]">{order?.id}</p>
      {order && <p className="mt-2 text-sm text-white/60">{t("cart.total")}: {fmtPrice(order.total, i18n.language)}</p>}
      <Link to="/eshop" data-testid="order-back-home" className="nv-btn-lime mt-8">{t("checkout.backHome")} <ArrowRight size={16} /></Link>
    </Box>
  );
  return (
    <Box>
      <XCircle size={56} className="mx-auto text-red-400" />
      <h1 data-testid="payment-pending" className="mt-6 font-display text-3xl font-bold">{state === "timeout" ? t("payment.pending") : t("payment.failed")}</h1>
      <p className="mt-3 text-white/70">{t("payment.pendingSub")}</p>
      <Link to="/account" className="nv-btn-lime mt-8">{t("nav.account")} <ArrowRight size={16} /></Link>
    </Box>
  );
}

export function PaymentCancel() {
  const { t } = useTranslation();
  return (
    <Box>
      <XCircle size={56} className="mx-auto text-white/60" />
      <h1 data-testid="payment-cancel" className="mt-6 font-display text-3xl font-bold">{t("payment.cancel")}</h1>
      <p className="mt-3 text-white/70">{t("payment.cancelSub")}</p>
      <Link to="/checkout" data-testid="payment-retry" className="nv-btn-lime mt-8">{t("payment.retry")} <ArrowRight size={16} /></Link>
    </Box>
  );
}
